import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileStack, ExternalLink, CheckCircle2, Send, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PRIORITIES } from "@/lib/constants";
import { TASK_TYPES, statusOptionsFor, sourceLink, fmtDate } from "@/lib/tasks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuthImage from "@/components/AuthImage";
import MediaUploader from "@/components/MediaUploader";
import { PriorityBadge, StatusBadge } from "@/components/tasks/TaskBadges";

const NONE = "__none";

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;
}

function Comments({ detail, onSent }) {
  const [text, setText] = useState("");
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = detail.task;
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${t.type}/${t.id}/comments`, { text, request_feedback: ask });
      setText(""); setAsk(false); toast.success(ask ? "Rückfrage gesendet" : "Kommentar gespeichert"); onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="task-comments">
      <div className="text-sm font-semibold text-slate-800">Kommentare & Rückmeldungen</div>
      {detail.comments.length === 0 && <div className="text-xs text-slate-400">Noch keine Kommentare.</div>}
      <div className="space-y-2 max-h-48 overflow-auto scroll-thin">
        {detail.comments.map((c) => (
          <div key={c.id} className={`text-sm rounded-lg px-3 py-2 border ${c.request_feedback ? "bg-violet-50 border-violet-200" : "bg-slate-50 border-slate-100"}`} data-testid={`task-comment-${c.id}`}>
            <div className="text-[11px] text-slate-500">{c.author} · {(c.created_at || "").slice(0, 16).replace("T", " ")}{c.request_feedback ? " · Rückfrage" : ""}</div>
            <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
          </div>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Kommentar oder Rückmeldung …" data-testid="task-comment-input" />
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={ask} onCheckedChange={(v) => setAsk(!!v)} data-testid="task-comment-request-feedback" />Rückmeldung vom Zuständigen anfordern</label>
        <Button size="sm" onClick={send} disabled={busy || !text.trim()} data-testid="task-comment-send"><Send className="h-3.5 w-3.5 mr-1" />Senden</Button>
      </div>
    </div>
  );
}

function Photos({ detail, onUploaded }) {
  const t = detail.task;
  if (t.type === "inspection") return null;
  const uploader = t.type === "defect"
    ? <MediaUploader compact links={{}} endpoint={`/defects/${t.id}/photos`} onUploaded={onUploaded} />
    : t.position_id ? <MediaUploader compact links={{}} endpoint={`/positions/${t.position_id}/files`} extraFields={{ ticket_id: t.id, category: "photo" }} onUploaded={onUploaded} /> : null;
  return (
    <div className="space-y-2" data-testid="task-photos">
      <div className="text-sm font-semibold text-slate-800">Fotos ({detail.photos.length})</div>
      {detail.photos.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {detail.photos.map((f) => <AuthImage key={f.id} fileId={f.id} thumb className="aspect-square w-full rounded-lg object-cover border border-slate-200" data-testid={`task-photo-${f.id}`} />)}
        </div>
      )}
      {detail.can_edit && uploader}
    </div>
  );
}

export default function TaskDetailDialog({ taskRef, onClose, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/tasks/${taskRef.type}/${taskRef.id}`).then((r) => {
      setDetail(r.data); setError("");
      const t = r.data.task;
      setForm({ status: t.status, priority: t.priority, due_date: t.due_date || "", assignee_id: t.assignee_id || "" });
      if (r.data.can_assign) api.get("/tasks/assignees", { params: { project_id: t.project_id, handover: r.data.handover_only || undefined } }).then((m) => setMembers(m.data)).catch(() => {});
    }).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [taskRef]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data: res } = await api.patch(`/tasks/${taskRef.type}/${taskRef.id}`, patch);
      if ("assignee_id" in patch && (res?.assignee_id || "") !== (patch.assignee_id || "")) {
        toast.error("Zuständigkeit wurde nicht übernommen. Bitte erneut versuchen."); load(); return;
      }
      onChanged();
      if (detail?.handover_only && patch.assignee_id && patch.assignee_id !== detail.me) { toast.success("Aufgabe weitergegeben"); onClose(); return; }
      toast.success("Aufgabe gespeichert"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const t = detail?.task;
  const closeStatus = t?.type === "defect" ? "behoben" : "erledigt";
  const link = t && sourceLink(t);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = t && (form.status !== t.status || form.priority !== t.priority || form.due_date !== (t.due_date || "") || form.assignee_id !== (t.assignee_id || ""));
  const submit = () => {
    const p = {};
    if (form.status !== t.status && t.type !== "inspection") p.status = form.status;
    if (form.priority !== t.priority) p.priority = form.priority;
    if (form.due_date !== (t.due_date || "")) p.due_date = form.due_date;
    if (form.assignee_id !== (t.assignee_id || "")) p.assignee_id = form.assignee_id;
    save(p);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="task-detail-dialog">
        {error && <div className="text-sm text-red-600 p-4" data-testid="task-detail-error">{error}</div>}
        {!t && !error && <div className="h-40 animate-pulse bg-slate-100 rounded-lg" />}
        {t && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap"><PriorityBadge p={t.priority} /><StatusBadge t={t} /><span className="text-xs text-slate-500">{TASK_TYPES[t.type]}{t.number ? ` · ${t.number}` : ""}</span></div>
              <DialogTitle className="text-left" data-testid="task-detail-title">{t.title || "(ohne Titel)"}</DialogTitle>
              <DialogDescription className="text-left">{[t.project_name, t.building, t.floor, t.room].filter(Boolean).join(" · ") || "Kein Objekt"} · erstellt {fmtDate(t.created_at)} · aktualisiert {fmtDate(t.updated_at)}</DialogDescription>
            </DialogHeader>
            {t.description && <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="task-detail-description">{t.description}</p>}
            <div className="flex flex-wrap gap-2">
              {t.plan_id && t.position_id && <Button variant="outline" size="sm" onClick={() => navigate(link)} data-testid="task-open-plan"><FileStack className="h-4 w-4 mr-1" />Im Bauplan öffnen{t.position_number ? ` (Marker ${t.position_number})` : ""}</Button>}
              {link && !(t.plan_id && t.position_id) && <Button variant="outline" size="sm" onClick={() => navigate(link)} data-testid="task-open-source"><ExternalLink className="h-4 w-4 mr-1" />{t.type === "inspection" ? "In Prüfungen öffnen / abschließen" : "Vorgang öffnen"}</Button>}
              {t.type !== "inspection" && !t.is_closed && detail.can_edit && <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={saving} onClick={() => save({ status: closeStatus })} data-testid="task-close-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Abschließen</Button>}
              {t.is_closed && t.type !== "inspection" && detail.can_edit && <Button size="sm" variant="outline" disabled={saving} onClick={() => save({ status: "offen" })} data-testid="task-reopen-btn">Wieder öffnen</Button>}
              {!detail.can_assign && !t.assignee_id && <Button size="sm" variant="outline" disabled={saving} onClick={() => save({ assignee_id: detail.me })} data-testid="task-take-btn"><UserPlus className="h-4 w-4 mr-1" />Übernehmen</Button>}
            </div>
            {detail.can_edit ? (
              <div className="grid sm:grid-cols-2 gap-3 border border-slate-200 rounded-xl p-3" data-testid="task-edit-form">
                {t.type !== "inspection" && (
                  <Field label="Status">
                    <Select value={form.status} onValueChange={set("status")}><SelectTrigger data-testid="task-edit-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{statusOptionsFor(t.type).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                  </Field>
                )}
                <Field label="Priorität">
                  <Select value={form.priority} onValueChange={set("priority")}><SelectTrigger data-testid="task-edit-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{[...PRIORITIES].reverse().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label="Fällig am"><Input type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="task-edit-due" /></Field>
                <Field label="Verantwortlich">
                  {detail.can_assign ? (
                    <Select value={form.assignee_id || NONE} onValueChange={(v) => { if (v) { set("assignee_id")(v === NONE ? "" : v); } }}>
                      <SelectTrigger data-testid="task-edit-assignee"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {!detail.handover_only && <SelectItem value={NONE}>— nicht zugewiesen —</SelectItem>}
                        {t.assignee_id && !members.some((m) => m.user_id === t.assignee_id) && <SelectItem value={t.assignee_id} disabled data-testid="task-assignee-previous">Bisher zuständig (nicht wählbar)</SelectItem>}
                        {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <div className="h-9 flex items-center text-sm text-slate-700" data-testid="task-assignee-readonly">{t.assignee_name || t.legacy_responsible || "nicht zugewiesen"}</div>}
                </Field>
                <div className="sm:col-span-2 flex justify-end"><Button size="sm" disabled={!dirty || saving} onClick={submit} data-testid="task-edit-save">Änderungen speichern</Button></div>
              </div>
            ) : <div className="text-xs text-slate-500" data-testid="task-readonly-hint">Dieser Vorgang ist Ihnen nicht zugewiesen – übernehmen Sie ihn, um Status, Frist und Fotos zu bearbeiten. Kommentare sind möglich.</div>}
            <Photos detail={detail} onUploaded={() => { load(); onChanged(); }} />
            <Comments detail={detail} onSent={() => { load(); onChanged(); }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
